import { useState, useMemo } from "react";
import { ShoppingCart, Plus, Minus, Trash2, Coffee } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useCart } from "@/contexts/CartContext";
import { useNavigate } from "react-router-dom";

// Top 3 best-selling coffees for upselling
const TOP_COFFEES = [
  { id: "cafe-solo", name: "Café Solo", price: 1.20, category: "Bebidas Calientes" },
  { id: "cafe-con-leche", name: "Café con Leche", price: 1.40, category: "Bebidas Calientes" },
  { id: "cortado", name: "Cortado", price: 1.30, category: "Bebidas Calientes" },
];

// Categories that trigger upselling
const UPSELL_TRIGGER_CATEGORIES = ["Pastelería", "Sándwiches", "Salado"];
const DRINK_CATEGORIES = ["Bebidas Calientes", "Bebidas Frías"];

const FloatingCart = () => {
  const { items, total, itemCount, updateQuantity, removeItem, addItem } = useCart();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const formatPrice = (price: number) => {
    return price.toFixed(2).replace(".", ",") + "€";
  };

  const handleCheckout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  // Check if upselling should be shown
  const showUpsell = useMemo(() => {
    const hasTriggerCategory = items.some(item => 
      UPSELL_TRIGGER_CATEGORIES.some(cat => 
        item.category?.toLowerCase().includes(cat.toLowerCase())
      )
    );
    const hasDrink = items.some(item => 
      DRINK_CATEGORIES.some(cat => 
        item.category?.toLowerCase().includes(cat.toLowerCase())
      )
    );
    return hasTriggerCategory && !hasDrink;
  }, [items]);

  const handleAddCoffee = (coffee: typeof TOP_COFFEES[0]) => {
    addItem({
      id: coffee.id,
      name: coffee.name,
      price: coffee.price,
      category: coffee.category,
    });
  };

  if (itemCount === 0) return null;

  return (
    <>
      {/* Floating Button */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button
            size="lg"
            className="fixed bottom-6 right-6 z-50 h-16 w-16 rounded-full shadow-lg bg-secondary hover:bg-secondary/90 text-secondary-foreground md:hidden"
          >
            <div className="relative">
              <ShoppingCart className="h-6 w-6" />
              <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                {itemCount}
              </span>
            </div>
          </Button>
        </SheetTrigger>

        {/* Desktop sticky bar */}
        <div className="hidden md:flex fixed bottom-0 left-0 right-0 z-50 bg-card border-t border-border shadow-lg p-4">
          <div className="container mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <ShoppingCart className="h-6 w-6 text-secondary" />
              <span className="font-medium text-primary">
                {itemCount} producto{itemCount !== 1 ? "s" : ""} en tu cesta
              </span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-2xl font-bold text-secondary">
                {formatPrice(total)}
              </span>
              <SheetTrigger asChild>
                <Button variant="outline">Ver cesta</Button>
              </SheetTrigger>
              <Button onClick={handleCheckout} className="bg-secondary hover:bg-secondary/90 text-secondary-foreground">
                Pagar ahora
              </Button>
            </div>
          </div>
        </div>

        <SheetContent side="bottom" className="h-[85vh] rounded-t-3xl">
          <SheetHeader className="pb-4 border-b border-border">
            <SheetTitle className="flex items-center gap-2 text-primary">
              <ShoppingCart className="h-5 w-5 text-secondary" />
              Tu Cesta ({itemCount})
            </SheetTitle>
          </SheetHeader>

          <ScrollArea className="flex-1 h-[calc(100%-220px)] py-4">
            <div className="space-y-4">
            {items.map((item, index) => {
                // Create unique key for items with customizations
                const itemKey = item.removedIngredients?.length 
                  ? `${item.id}-${item.removedIngredients.join(",")}-${index}`
                  : `${item.id}-${index}`;
                
                return (
                  <div
                    key={itemKey}
                    className="flex items-center gap-4 p-4 bg-muted/30 rounded-xl"
                  >
                    <div className="flex-1">
                      <h4 className="font-medium text-primary">{item.name}</h4>
                      {item.removedIngredients && item.removedIngredients.length > 0 && (
                        <p className="text-xs text-orange-600 font-medium mt-0.5">
                          ({item.removedIngredients.map(i => `Sin ${i}`).join(", ")})
                        </p>
                      )}
                      <p className="text-sm text-muted-foreground">{item.category}</p>
                      <p className="font-semibold text-secondary mt-1">
                        {formatPrice(item.price * item.quantity)}
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-10 w-10 rounded-full"
                        onClick={() => updateQuantity(item.id, item.quantity - 1, item.removedIngredients)}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="w-8 text-center font-bold text-lg">
                        {item.quantity}
                      </span>
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-10 w-10 rounded-full"
                        onClick={() => updateQuantity(item.id, item.quantity + 1, item.removedIngredients)}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-10 w-10 text-destructive hover:text-destructive"
                        onClick={() => removeItem(item.id, item.removedIngredients)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>

          <div className="absolute bottom-0 left-0 right-0 p-6 bg-card border-t border-border">
            {/* Upselling Banner */}
            {showUpsell && (
              <div className="mb-4 p-3 bg-secondary/10 border border-secondary/20 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Coffee className="h-4 w-4 text-secondary" />
                  <span className="text-sm font-medium text-primary">
                    ¿Te apetece un café para acompañar?
                  </span>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {TOP_COFFEES.map((coffee) => (
                    <Button
                      key={coffee.id}
                      size="sm"
                      variant="outline"
                      className="text-xs h-8 border-secondary/30 hover:bg-secondary/20"
                      onClick={() => handleAddCoffee(coffee)}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      {coffee.name} ({formatPrice(coffee.price)})
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between mb-4">
              <span className="text-lg font-medium text-primary">Total</span>
              <span className="text-2xl font-bold text-secondary">
                {formatPrice(total)}
              </span>
            </div>
            <Button
              size="lg"
              className="w-full h-14 text-lg bg-secondary hover:bg-secondary/90 text-secondary-foreground"
              onClick={handleCheckout}
            >
              Pagar ahora
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default FloatingCart;
